import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { auth, db } from '../services/firebase';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { onAuthStateChanged, signInWithEmailAndPassword, signOut, createUserWithEmailAndPassword, sendPasswordResetEmail, GoogleAuthProvider, signInWithPopup, setPersistence, browserLocalPersistence } from 'firebase/auth';
import { requestNotificationPermission } from '../services/notificationService';
import ModalAlert from '../components/modal/ModalAlert'; // Importar el componente ModalAlert
import { Box, CircularProgress } from '@mui/material';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [alert, setAlert] = useState({ open: false, message: '' }); // Estado para manejar la alerta

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                try {
                    const userDoc = await getDoc(doc(db, 'users', user.uid));
                    if (!userDoc.exists()) {
                        // Si el usuario no existe en Firestore, mostrar un mensaje
                        setAlert({
                            open: true,
                            message: 'Tu cuenta no existe. Por favor, contacta al administrador.',
                        });
                        setCurrentUser(null);
                    } else if (!userDoc.data().enabled) {
                        // Si el usuario no está habilitado, mostrar un mensaje pero no cerrar sesión
                        setAlert({
                            open: true,
                            message: 'Tu cuenta aún no ha sido habilitada. Por favor, espera a que un administrador la habilite.',
                        });
                        setCurrentUser({ ...user, ...userDoc.data() });
                    } else {
                        // Si el usuario existe y está habilitado, establecerlo como usuario actual
                        setCurrentUser({ ...user, ...userDoc.data() });
                    }
                } catch (error) {
                    console.error('Error al verificar el usuario:', error);
                    setAlert({
                        open: true,
                        message: 'Ocurrió un error al verificar tu cuenta. Por favor, intenta nuevamente.',
                    });
                    setCurrentUser(null);
                }
            } else {
                setCurrentUser(null);
            }
            setLoading(false);
        });

        return unsubscribe;
    }, []);

    // Function to manually refresh user data from Firestore
    const refreshUserData = useCallback(async () => {
        if (auth.currentUser) {
            try {
                const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
                if (userDoc.exists()) {
                    setCurrentUser({ ...auth.currentUser, ...userDoc.data() });
                }
            } catch (error) {
                console.error('Error refreshing user data:', error);
            }
        }
    }, []);



    const login = useCallback(async (email, password) => {
        const result = await signInWithEmailAndPassword(auth, email, password);
        const userDoc = await getDoc(doc(db, 'users', result.user.uid));

        if (userDoc.exists() && !userDoc.data().enabled) {
            throw new Error('Tu cuenta aún no ha sido habilitada. Por favor, espera a que un administrador la habilite.');
        }

        // Solicitar permisos de notificación después de iniciar sesión
        const token = await requestNotificationPermission();
        if (token) {
            console.log('Token de notificación obtenido:', token);
            // Aquí puedes guardar el token en Firestore o usarlo según sea necesario
        }

        return result;
    }, []);

    const register = async (email, password, firstName, lastName) => {
        const result = await createUserWithEmailAndPassword(auth, email, password);
        const userDocRef = doc(db, 'users', result.user.uid);

        await setDoc(userDocRef, {
            email: result.user.email,
            firstName: firstName,
            lastName: lastName,
            role: 'user', // Rol predeterminado
            createdAt: new Date(),
            enabled: false, // El usuario no está habilitado por defecto
            license: {
                type: 'free', // Tipo de cuenta inicial
                accountLimit: 3, // Límite de cuentas para la cuenta gratuita
                transactionLimit: 100, // Límite de transacciones para la cuenta gratuita
                accountsCreated: 0, // Contador inicial de cuentas creadas
                transactionsCreated: 0, // Contador inicial de transacciones creadas
                expirationDate: new Date(new Date().setFullYear(new Date().getFullYear() + 1)), // 1 año de licencia gratuita
            },
        });

        // Solicitar permisos de notificación después de registrar al usuario
        const token = await requestNotificationPermission();
        if (token) {
            console.log('Token de notificación obtenido:', token);
            await setDoc(userDocRef, { notificationToken: token }, { merge: true }); // Guardar el token en Firestore
        }

        return result;
    };

    const resetPassword = async (email) => {
        try {
            await sendPasswordResetEmail(auth, email);
            console.log(`Password reset email sent to ${email}`);
        } catch (error) {
            console.error('Error sending password reset email:', error);
            throw error;
        }
    };

    const loginWithGoogle = useCallback(async () => {
        const provider = new GoogleAuthProvider();
        provider.setCustomParameters({
            prompt: 'select_account', // Forzar a que se pregunte por la cuenta de Google
        });

        setLoading(true); // Activar el estado de carga

        try {
            // Configurar la persistencia de la sesión
            await setPersistence(auth, browserLocalPersistence);

            const result = await signInWithPopup(auth, provider);
            const user = result.user;

            // Referencia al documento del usuario en Firestore
            const userDocRef = doc(db, 'users', user.uid);
            const userDoc = await getDoc(userDocRef);

            if (!userDoc.exists()) {
                // Si el usuario no existe, crearlo en Firestore
                await setDoc(userDocRef, {
                    email: user.email,
                    displayName: user.displayName,
                    photoURL: user.photoURL,
                    role: 'user', // Rol predeterminado
                    createdAt: new Date(),
                    enabled: false, // El usuario no está habilitado por defecto
                    license: {
                        type: 'free', // Tipo de licencia inicial
                        accountLimit: 3, // Límite de cuentas para la licencia gratuita
                        transactionLimit: 100, // Límite de transacciones para la licencia gratuita
                        accountsCreated: 0, // Contador inicial de cuentas creadas
                        transactionsCreated: 0, // Contador inicial de transacciones creadas
                        expirationDate: new Date(new Date().setFullYear(new Date().getFullYear() + 1)), // 1 año de licencia gratuita
                    },
                });
                // Establecer el usuario actual con los datos por defecto
                setCurrentUser({
                    ...user, ...{
                        role: 'user',
                        enabled: false
                    }
                });
            } else {
                // Si el usuario existe, verificar si está habilitado
                if (!userDoc.data().enabled) {
                    setAlert({
                        open: true,
                        message: 'Tu cuenta aún no ha sido habilitada. Por favor, espera a que un administrador la habilite.',
                    });
                    setCurrentUser({ ...user, ...userDoc.data() });
                    return; // Salir de la función sin errores
                }
                // Establecer el usuario actual
                setCurrentUser({ ...user, ...userDoc.data() });
            }

            // Solicitar permisos de notificación después de iniciar sesión con Google
            const token = await requestNotificationPermission();
            if (token) {
                console.log('Token de notificación obtenido:', token);
                await setDoc(userDocRef, { notificationToken: token }, { merge: true }); // Guardar el token en Firestore
            }

            setAlert({
                open: true,
                message: `Bienvenido a la aplicación. ${user.displayName}`,
            });
        } catch (error) {
            console.error('Error al iniciar sesión con Google:', error);
            setAlert({
                open: true,
                message: error.message || 'Ocurrió un error al iniciar sesión con Google. Por favor, intenta nuevamente.',
            });
        } finally {
            setLoading(false); // Desactivar el estado de carga
        }
    }, []);

    const logout = async () => {
        return await signOut(auth);
    };

    const forgotPassword = async (email) => {
        return await sendPasswordResetEmail(auth, email);
    };

    const isLogin = () => {
        return currentUser !== null;
    };

    const value = {
        currentUser,
        login,
        register,
        logout,
        forgotPassword,
        resetPassword, // Add here
        loginWithGoogle, // Add here
        refreshUserData, // Add here
        isLogin,
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', width: '100vw' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <AuthContext.Provider value={value}>
            {children}
            <ModalAlert
                open={alert.open}
                message={alert.message}
                onClose={() => setAlert({ open: false, message: '' })}
            />
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    return useContext(AuthContext);
};